import { Component, HostBinding, Input } from "@angular/core";

export type CoreUIIconSizePreset = "sm" | "md" | "base" | "lg";
export type CoreUIIconSize =
  | CoreUIIconSizePreset
  | number
  | `${number}`
  | `${number}px`;

const ICON_SIZE_PRESETS = new Set<CoreUIIconSizePreset>([
  "sm",
  "md",
  "base",
  "lg",
]);

@Component({
  selector: "core-ui-icon",
  templateUrl: "./core-ui-icon.component.html",
  styleUrls: ["./core-ui-icon.component.scss"],
  standalone: false,
})
export class CoreUIIconComponent {
  @Input() name!: string;

  @Input() size: CoreUIIconSize = "md";

  @Input() direction: "none" | "up" | "down" | "left" | "right" = "none";

  @Input() color?: string;

  @Input() preserveColors = false;

  @Input() extraClass = "";

  get iconPath(): string {
    return `assets/core-ui/icons/${this.name}.svg`;
  }

  @HostBinding("class")
  get hostClasses(): string {
    const sizeClass = this.isSizePreset(this.size)
      ? `core-ui-icon--${this.size}`
      : "";

    return [
      "core-ui-icon",
      sizeClass,
      `core-ui-icon--${this.direction}`,
      this.extraClass,
    ]
      .filter(Boolean)
      .join(" ");
  }

  @HostBinding("style.color")
  get hostColor(): string | null {
    return this.color ?? null;
  }

  @HostBinding("style.width")
  get hostWidth(): string | null {
    return this.customSize;
  }

  @HostBinding("style.height")
  get hostHeight(): string | null {
    return this.customSize;
  }

  private get customSize(): string | null {
    if (this.isSizePreset(this.size)) return null;

    const value =
      typeof this.size === "number"
        ? this.size
        : Number(String(this.size).replace(/px$/i, ""));

    return Number.isFinite(value) && value > 0 ? `${value}px` : null;
  }

  private isSizePreset(size: CoreUIIconSize): size is CoreUIIconSizePreset {
    return (
      typeof size === "string" &&
      ICON_SIZE_PRESETS.has(size as CoreUIIconSizePreset)
    );
  }
}
